import { useState, useRef, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { CombinedHealthCard, HealthPlanCardResponse } from '../../../api/ester-b2b-api/private/healthPlan/types'
import { BodyLarge, BodyLargeMedium } from '../../styles/Typography'
import { theme } from '../../../theme/theme'
import { HealthPlanCardList } from './HealthPlanCardList'
import { useEmitEvent } from '../../../hooks/analytics/useEmitEvent'
import { TrackingEvents } from '@estercare/ester-shared'

type Props = {
  cards: CombinedHealthCard[]
}

export const HealthPlanCardSection = ({ cards }: Props) => {
  const { t } = useTranslation()
  const [expandedCard, setExpandedCard] = useState<HealthPlanCardResponse | null>(null)
  const sectionHeaderRef = useRef<HTMLDivElement>(null)
  const emitEvent = useEmitEvent()
  const [searchParams, setSearchParams] = useSearchParams()
  const [shouldScrollToSection, setShouldScrollToSection] = useState(false)

  useEffect(() => {
    const moduleParam = searchParams.get('module')
    if (moduleParam && cards.length > 0) {
      const matchingCard = cards.find((card) => card.module === moduleParam)
      if (matchingCard) {
        setExpandedCard(matchingCard)
        setShouldScrollToSection(true)
        searchParams.delete('module')
        setSearchParams(searchParams, { replace: true })
      }
    }
  }, [cards, searchParams, setSearchParams])

  useEffect(() => {
    if (shouldScrollToSection && expandedCard) {
      requestAnimationFrame(() => {
        sectionHeaderRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
      setShouldScrollToSection(false)
    }
  }, [shouldScrollToSection, expandedCard])

  const handleCardClick = (card: HealthPlanCardResponse) => {
    setExpandedCard(card)
    emitEvent({
      eventType: TrackingEvents.OPEN_HEALTH_CARD,
      options: {
        metadata: {
          health_card: card.module,
        },
      },
    })
  }

  return (
    <Wrapper>
      <SectionHeader ref={sectionHeaderRef}>
        <SectionTitle>{t('employee.yourHealth.healthPlanCardsTitle')}</SectionTitle>
        <SectionDescription>{t('employee.yourHealth.healthPlanCardsDescription')}</SectionDescription>
      </SectionHeader>
      <HealthPlanCardList
        cards={cards}
        onCardClick={handleCardClick}
        expandedCard={expandedCard}
        onClose={() => setExpandedCard(null)}
        scrollTargetRef={sectionHeaderRef}
      />
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${theme.spacing.medium}px;
`

const SectionTitle = styled(BodyLargeMedium)`
  color: ${theme.color.black};
`

const SectionDescription = styled(BodyLarge)`
  color: ${theme.color.black};
`
